import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import SidePanel from "layouts/SidePanel";
import FilterSection from "./components/FilterPanel";
import NeevayNetworkHeader from "./components/NeevayNetworkHeader";
import SearchCategoryTable from "./components/SearchCategoryTable";
import Table from "./components/Table";

const NeevayNetwork = () => {
  const searchResult = useSelector((state) => state.search);
  const dispatch = useDispatch();
  const location = useLocation();

  let hasResults =
    searchResult?.results && searchResult?.results.length > 0;

  const hasKeyword = !!searchResult?.searchKeyWord;
  const isLoading = searchResult?.isSearching;
  const numFound = searchResult?.numFound ?? 0;

  const showNoResults =
    hasKeyword && !isLoading && numFound === 0 && !hasResults;
  const emptySearchLabel = searchResult.subCategory || searchResult.searchKeyWord;

  const [showCategories, setShowCategories] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedVendorTypeKey, setSelectedVendorTypeKey] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [hasAnimatedOnce, setHasAnimatedOnce] = useState(false);
  const [resultsToolbarSummaryTarget, setResultsToolbarSummaryTarget] =
    useState(null);
  const [resultsToolbarActionsTarget, setResultsToolbarActionsTarget] =
    useState(null);

  const isFirstLoad = useRef(true);
  const lastSearchSource = useRef("initial");

  const isResultsPage = showContent && (hasResults || showNoResults);

  const handleBackToMainSearch = () => {
    const params = new URLSearchParams(window.location.search);
    const restoreType = params.get("type");

    if (params.toString()) {
      params.set("restoreSelection", "1");
      window.history.pushState({}, "", `/neevay-network?${params.toString()}`);
    } else {
      window.history.pushState({}, "", "/neevay-network");
    }

    dispatch({ type: "SEARCH_RESET" });
    setShowCategories(true);
    setShowFilters(false);
    setShowContent(false);
    setHasAnimatedOnce(false);
    setSelectedVendorTypeKey(restoreType || "");
    isFirstLoad.current = true;
  };

  useEffect(() => {
    if (searchResult.searchKeyWord || searchResult.subCategory) {
      lastSearchSource.current = "search";
    }
  }, [searchResult]);

  useEffect(() => {
    if (location.pathname === "/neevay-network") {
      dispatch({ type: "SEARCH_RESET" });

      setShowCategories(true);
      setShowFilters(false);
      setShowContent(false);
      setHasAnimatedOnce(false);
      isFirstLoad.current = true;
    }
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;

      if (hasResults) {
        setShowCategories(false);
        setShowContent(true);
      }
      return;
    }

    if (lastSearchSource.current === "drawer" && showNoResults) {
      setShowCategories(false);
      setShowContent(true);
      return;
    }

    const shouldAnimate =
      (hasResults || showNoResults) &&
      showCategories &&
      !hasAnimatedOnce &&
      lastSearchSource.current !== "drawer";

    if (shouldAnimate) {
      setIsAnimating(true);
      setShowContent(false);

      setTimeout(() => {
        setShowCategories(false);
        setIsAnimating(false);

        setShowContent(true);

        setHasAnimatedOnce(true);
      }, 1000);
    } else if (!hasResults && !showNoResults) {
      setShowCategories(true);
      setShowContent(false);
    } else if (hasResults) {
      setShowCategories(false);
      setShowContent(true);
    }
  }, [hasResults, showNoResults, showCategories, hasAnimatedOnce]);

  return (
    <SidePanel>
      <div className="flex h-full min-h-0 w-full flex-col bg-[radial-gradient(circle_at_top_left,rgba(253,102,0,0.16),transparent_26%),radial-gradient(circle_at_top_right,rgba(228,231,255,0.95),transparent_34%),linear-gradient(135deg,#FFF7F1_0%,#FFFDFB_58%,#F4F6FF_100%)]">
        <NeevayNetworkHeader
          selectedVendorTypeKey={selectedVendorTypeKey}
          onVendorTypeChange={setSelectedVendorTypeKey}
          showCategories={showCategories}
          showNoResults={showNoResults}
          hideSearchSection={isResultsPage}
          showSearchByCategory={isResultsPage}
          onBackToMainSearch={handleBackToMainSearch}
          showResultsToolbar={isResultsPage}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters((prev) => !prev)}
          onResultsToolbarSummaryMount={setResultsToolbarSummaryTarget}
          onResultsToolbarActionsMount={setResultsToolbarActionsTarget}
        />

        {(showCategories || isLoading) && !hasAnimatedOnce && (
          <div
            className={`h-full px-3 pb-4 pt-2.5 transition-all ease-in-out lg:px-4 ${
              isAnimating
                ? "translate-x-full scale-95 transform opacity-0"
                : "translate-x-0 scale-100 transform opacity-100"
            }`}
            style={{ transitionDuration: "2200ms" }}
          >
            <div className="overflow-hidden rounded-md border border-[#F1DDD2] bg-white/85 shadow-[0_24px_64px_rgba(31,30,41,0.08)] backdrop-blur">
              <SearchCategoryTable allowedCategoryType={selectedVendorTypeKey === "all" ? "all" : selectedVendorTypeKey || "none"} />
            </div>
          </div>
        )}

        {(showCategories || isLoading) &&
          hasAnimatedOnce &&
          !hasResults &&
          !showNoResults && (
            <div className="h-full overflow-y-auto px-3 pb-4 pt-2.5 lg:px-4">
              <div className="overflow-hidden rounded-md border border-[#F1DDD2] bg-white/85 shadow-[0_24px_64px_rgba(31,30,41,0.08)] backdrop-blur">
                <SearchCategoryTable allowedCategoryType={selectedVendorTypeKey === "all" ? "all" : selectedVendorTypeKey || "none"} />
              </div>
            </div>
          )}

        {showNoResults && !showContent && (
          <div
            className={`px-3 pb-4 pt-2.5 transition-all ease-in-out transform lg:px-4 ${
              isAnimating
                ? "translate-x-full opacity-0"
                : "translate-x-0 opacity-100"
            }`}
            style={{ transitionDuration: "1200ms" }}
          >
            <div className="flex h-[calc(100vh-160px)] w-full flex-col items-center justify-center rounded-[28px] border border-[#F1DDD2] bg-[linear-gradient(180deg,#FFF9F5_0%,#FFFFFF_50%)] p-10 text-center shadow-[0_24px_64px_rgba(31,30,41,0.08)]">
              <img
                src="https://storagereponeevaydevcdn.blob.core.windows.net/business/Search_image.png"
                className="mb-6 h-[116px] w-[122px]"
                alt="No search results"
              />
              <p className="text-md text-center font-bold text-[#3C3C3C]">
                No results found for{" "}
                <span className="text-[#FD6600]">
                  "{emptySearchLabel}"
                </span>{" "}
                in{" "}
                <span className="text-[#FD6600]">
                  {searchResult.regionString}
                </span>
              </p>
              <p className="mt-2 text-center text-sm text-gray-600">
                Try selecting a different city or browse other subcategories
              </p>
            </div>
          </div>
        )}

        {showContent && (hasResults || showNoResults) && (
          <div className="mt-1 flex min-h-0 flex-1 flex-col gap-2 px-3 pb-4 lg:px-4">
            <div className="flex min-h-0 flex-1 items-stretch gap-4">
              {showFilters && (
                <div className="h-full min-h-0 w-[280px] shrink-0">
                  <FilterSection />
                </div>
              )}

              <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
                <Table
                  showFilters={showFilters}
                  onToggleFilters={() => setShowFilters((prev) => !prev)}
                  toolbarSummaryPortalTarget={resultsToolbarSummaryTarget}
                  toolbarActionsPortalTarget={resultsToolbarActionsTarget}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </SidePanel>
  );
};

export default NeevayNetwork;





